import type { MessageFile, StagedAttachment } from "@services/drybulkCargoOperationsApi";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MESSAGE_DRAFTS_PERSIST_KEY } from "../constants";

export interface PendingAttach {
  files: MessageFile[];
  needsRepick: boolean;
}

export interface MessageDraft {
  text: string;
  staged: StagedAttachment[];
  pendingAttach: Record<string, PendingAttach>;
}

interface MessageDraftState {
  drafts: Record<string, MessageDraft>;
  selectedThread: Record<string, string>;
  setText: (key: string, text: string) => void;
  addStaged: (key: string, staged: StagedAttachment) => void;
  removeStaged: (key: string, documentUrl: string) => void;
  clearComposer: (key: string) => void;
  setPendingAttach: (key: string, messageId: string, value: PendingAttach | null) => void;
  completeSend: (key: string, messageId: string, pending: PendingAttach | null) => void;
  selectThread: (operationId: string, threadKey: string) => void;
}

export const messageDraftKey = (operationId: string, threadKey: string) =>
  `${operationId}:${threadKey}`;

const EMPTY_DRAFT: MessageDraft = { text: "", staged: [], pendingAttach: {} };

const isEmpty = (draft: MessageDraft) =>
  draft.text === "" && draft.staged.length === 0 && Object.keys(draft.pendingAttach).length === 0;

const withDraft = (
  drafts: Record<string, MessageDraft>,
  key: string,
  update: (draft: MessageDraft) => MessageDraft,
): Record<string, MessageDraft> => {
  const next = update(drafts[key] ?? EMPTY_DRAFT);
  const { [key]: removed, ...rest } = drafts;
  void removed;
  return isEmpty(next) ? rest : { ...rest, [key]: next };
};

const withoutPending = (pending: Record<string, PendingAttach>, messageId: string) => {
  const { [messageId]: removed, ...rest } = pending;
  void removed;
  return rest;
};

export const useMessageDraftStore = create<MessageDraftState>()(
  persist(
    (set) => ({
      drafts: {},
      selectedThread: {},
      setText: (key, text) =>
        set((state) => ({ drafts: withDraft(state.drafts, key, (d) => ({ ...d, text })) })),
      addStaged: (key, staged) =>
        set((state) => ({
          drafts: withDraft(state.drafts, key, (d) => ({ ...d, staged: [...d.staged, staged] })),
        })),
      removeStaged: (key, documentUrl) =>
        set((state) => ({
          drafts: withDraft(state.drafts, key, (d) => ({
            ...d,
            staged: d.staged.filter((s) => s.documentUrl !== documentUrl),
          })),
        })),
      clearComposer: (key) =>
        set((state) => ({
          drafts: withDraft(state.drafts, key, (d) => ({ ...d, text: "", staged: [] })),
        })),
      setPendingAttach: (key, messageId, value) =>
        set((state) => ({
          drafts: withDraft(state.drafts, key, (d) => ({
            ...d,
            pendingAttach: value
              ? { ...d.pendingAttach, [messageId]: value }
              : withoutPending(d.pendingAttach, messageId),
          })),
        })),
      completeSend: (key, messageId, pending) =>
        set((state) => ({
          drafts: withDraft(state.drafts, key, (d) => ({
            text: "",
            staged: [],
            pendingAttach: pending ? { ...d.pendingAttach, [messageId]: pending } : d.pendingAttach,
          })),
        })),
      selectThread: (operationId, threadKey) =>
        set((state) => ({ selectedThread: { ...state.selectedThread, [operationId]: threadKey } })),
    }),
    { name: MESSAGE_DRAFTS_PERSIST_KEY },
  ),
);
